import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Zap, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Spinner } from "../components/common";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const { login, register, verifyMfa } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const res = await login(email, password);
        if (res?.mfa_required) {
          setMfa({ ticket: res.mfa_ticket, method: res.mfa_method });
          toast.info(res.mfa_method === "telegram" ? "Código enviado no seu Telegram" : "Abra seu app autenticador");
          return;
        }
        toast.success("Bem-vindo de volta!");
      } else {
        await register(name, email, password);
        toast.success("Conta criada! Ganhou 30 GB de espaço NVMe por 15 dias de teste grátis.");
      }
      navigate("/app/bots");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async () => {
    setLoading(true);
    try {
      await verifyMfa(mfa.ticket, mfaCode.trim());
      toast.success("Bem-vindo de volta!");
      navigate("/app/bots");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="bg-scene" />
      <div className="w-full max-w-md anim-fade-up">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Zap size={18} className="text-slate-950" strokeWidth={2.5} />
          </div>
          <span className="font-display font-bold text-white text-lg tracking-tight">CINE<span className="neon-text">BOT</span></span>
        </div>

        <div className="glass rounded-3xl p-8">
          {!mfa && (
          <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-slate-950/60 border border-slate-800 mb-7">
            <button
              data-testid="auth-tab-login"
              onClick={() => setMode("login")}
              className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${mode === "login" ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" : "text-slate-400 hover:text-white border border-transparent"}`}
            >
              Entrar
            </button>
            <button
              data-testid="auth-tab-register"
              onClick={() => setMode("register")}
              className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${mode === "register" ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" : "text-slate-400 hover:text-white border border-transparent"}`}
            >
              Criar conta de revenda
            </button>
          </div>
          )}

          {mode === "register" && !mfa && (
            <div data-testid="auth-bonus-notice" className="mb-5 rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-4 py-3 text-xs text-emerald-300 flex items-center gap-2.5">
              <Gift size={15} className="shrink-0" />
              <span><b className="text-emerald-200">Teste grátis:</b> 30 GB de espaço NVMe por 15 dias, sem cartão. Depois é só escolher um plano — todos com <b className="text-emerald-200">streaming ilimitado</b>.</span>
            </div>
          )}

          {mfa ? (
            <div className="space-y-4" data-testid="mfa-step">
              <div className="text-center">
                <ShieldCheck size={28} className="text-cyan-400 mx-auto mb-2" />
                <p className="text-sm text-white font-semibold" data-testid="mfa-title">Verificação em 2 etapas</p>
                <p className="text-xs text-slate-400 mt-1">
                  {mfa.method === "telegram" ? "Enviamos um código de 6 dígitos no seu Telegram." : "Abra o Google Authenticator e digite o código de 6 dígitos."}
                </p>
              </div>
              <input data-testid="mfa-code-input" inputMode="numeric" autoComplete="one-time-code" className="input-dark w-full rounded-xl px-4 py-3 text-center text-lg tracking-[0.4em] font-mono2" placeholder="••••••" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} />
              <button data-testid="mfa-verify-btn" onClick={submitMfa} disabled={loading || mfaCode.trim().length < 6} className="btn-neon w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2">
                {loading ? <Spinner /> : "Verificar e entrar"}
              </button>
              <button data-testid="mfa-back-btn" onClick={() => { setMfa(null); setMfaCode(""); }} className="w-full text-xs text-slate-500 hover:text-slate-300 transition-colors">
                ← Voltar e tentar novamente
              </button>
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Nome</label>
                <input data-testid="auth-name-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Seu nome" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
            )}
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Email</label>
              <input data-testid="auth-email-input" type="email" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="voce@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Senha</label>
              <input data-testid="auth-password-input" type="password" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
            </div>
            <button data-testid="auth-submit-btn" type="submit" disabled={loading} className="btn-neon w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2">
              {loading ? <Spinner /> : mode === "login" ? "Entrar na plataforma" : "Criar minha conta"}
            </button>
          </form>
          )}

          {mode === "login" && !mfa && (
            <div className="mt-5 text-center">
              <Link data-testid="forgot-password-link" to="/forgot-password" className="text-xs text-cyan-400/80 hover:text-cyan-300 transition-colors">
                Esqueci minha senha
              </Link>
            </div>
          )}
        </div>

        <p className="text-center mt-6">
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">← Voltar para o site</Link>
        </p>
      </div>
    </div>
  );
}
